import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  BarChart3,
  BookOpen,
  Boxes,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  School as SchoolIcon,
  ShoppingCart,
  Trash2,
  TrendingUp,
  Users,
  X,
  AlertTriangle,
  Star,
  Eye,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api, {
  formatApiError,
} from "@/lib/api";

import { useAuth } from "@/context/AuthContext";


/* =========================================================
   HELPERS
========================================================= */

const INR = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN")}`;

const safeArray = (value) =>
  Array.isArray(value) ? value : [];

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  } catch {
    return "—";
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return "—";
  }
};

const getErrorMessage = (error) =>
  formatApiError(
    error?.response?.data?.detail ||
      error?.message
  );

const emptySchool = {
  name: "",
  short_name: "",
  description: "",
  color: "#8C9A86",
};

const emptyClass = {
  school_id: "",
  name: "",
  section: "",
  academic_year: "",
  active: true,
};

const emptyStudent = {
  school_id: "",
  class_id: "",
  student_name: "",
  parent_name: "",
  phone: "",
  email: "",
  address: "",
  discount_percent: 0,
  active: true,
};

const emptyProduct = {
  school_id: "",
  name: "",
  category: "",
  price: 0,
  sizes: [],
  variants: [],
  image: "",
  images: [],
  description: "",
  additional_info: "",
  size_chart: [],
  stock_quantity: 0,
  low_stock_threshold: 5,
  stock_tracking: false,
  in_stock: true,
  order: 0,
};


/* =========================================================
   UI COMPONENTS
========================================================= */

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled = false,
  className = "",
}) {
  const variants = {
    primary:
      "bg-foreground text-background hover:opacity-90",
    secondary:
      "border border-border bg-background hover:bg-secondary",
    danger:
      "bg-destructive text-destructive-foreground hover:opacity-90",
    ghost:
      "hover:bg-secondary",
    success:
      "bg-green-600 text-white hover:bg-green-700",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
        variants[variant] || variants.primary,
        className
      )}
    >
      {children}
    </button>
  );
}

function Input({
  label,
  value,
  onChange,
  placeholder = "",
  type = "text",
  required = false,
  disabled = false,
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {required && (
            <span className="text-destructive ml-1">
              *
            </span>
          )}
        </label>
      )}

      <input
        type={type}
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value)
        }
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-foreground disabled:opacity-50"
      />
    </div>
  );
}

function Textarea({
  label,
  value,
  onChange,
  placeholder = "",
  rows = 4,
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-sm font-medium">
          {label}
        </label>
      )}

      <textarea
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value)
        }
        placeholder={placeholder}
        rows={rows}
        className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-foreground"
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  children,
  required = false,
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {required && (
            <span className="text-destructive ml-1">
              *
            </span>
          )}
        </label>
      )}

      <select
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value)
        }
        required={required}
        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-foreground"
      >
        {children}
      </select>
    </div>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(e) =>
          onChange(e.target.checked)
        }
        className="h-4 w-4 rounded"
      />
      {label}
    </label>
  );
}

function Badge({
  children,
  tone = "neutral",
}) {
  const tones = {
    neutral:
      "bg-secondary text-secondary-foreground",
    green:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    red:
      "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    yellow:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    blue:
      "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  };

  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
        tones[tone] || tones.neutral
      )}
    >
      {children}
    </span>
  );
}

function Modal({
  open,
  onClose,
  title,
  children,
  wide = false,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div
        className={cn(
          "max-h-[92vh] w-full overflow-hidden rounded-2xl border border-border bg-background shadow-2xl",
          wide
            ? "max-w-5xl"
            : "max-w-2xl"
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-lg font-semibold">
            {title}
          </h2>

          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-secondary"
          >
            <X size={20} />
          </button>
        </div>

        <div className="max-h-[calc(92vh-70px)] overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  icon: Icon = Package,
  title,
  description,
}) {
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center text-center">
      <div className="mb-4 rounded-full bg-secondary p-4">
        <Icon size={28} />
      </div>

      <h3 className="font-semibold">
        {title}
      </h3>

      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
}) {
  return (
    <div className="rounded-2xl border border-border bg-background p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {label}
          </p>

          <p className="mt-2 text-2xl font-semibold tracking-tight">
            {value}
          </p>

          {subtitle && (
            <p className="mt-1 text-xs text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>

        <div className="rounded-xl bg-secondary p-3">
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  description,
  action,
}) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-xl font-semibold">
          {title}
        </h2>

        {description && (
          <p className="mt-1 text-sm text-muted-foreground">
            {description}
          </p>
        )}
      </div>

      {action}
    </div>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder = "Search...",
}) {
  return (
    <div className="relative w-full sm:max-w-sm">
      <Search
        size={17}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
      />

      <input
        value={value}
        onChange={(e) =>
          onChange(e.target.value)
        }
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-foreground"
      />
    </div>
  );
}

function LoadingBox() {
  return (
    <div className="flex min-h-[250px] items-center justify-center">
      <RefreshCw
        className="animate-spin text-muted-foreground"
        size={25}
      />
    </div>
  );
}


/* =========================================================
   MAIN ADMIN DASHBOARD
========================================================= */

export default function AdminDashboard() {
  const navigate = useNavigate();
  const {
    user,
    ready,
    logout,
  } = useAuth();

  const [mobileMenu, setMobileMenu] =
    useState(false);

  const [activeTab, setActiveTab] =
    useState("overview");

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [stats, setStats] =
    useState({});

  const [analytics, setAnalytics] =
    useState({});

  const [schools, setSchools] =
    useState([]);

  const [classes, setClasses] =
    useState([]);

  const [products, setProducts] =
    useState([]);

  const [students, setStudents] =
    useState([]);

  const [orders, setOrders] =
    useState([]);

  const [enquiries, setEnquiries] =
    useState([]);

  const [reviews, setReviews] =
    useState([]);

  const [error, setError] =
    useState("");

  const [modal, setModal] =
    useState(null);

  const [editing, setEditing] =
    useState(null);

  const [search, setSearch] =
    useState("");

  /* -------------------------------------------------------
     AUTH
  ------------------------------------------------------- */

  useEffect(() => {
    if (!ready) return;

    if (!user) {
      navigate(
        "/admin/login",
        { replace: true }
      );
    }
  }, [
    ready,
    user,
    navigate,
  ]);

  /* -------------------------------------------------------
     LOAD DATA
  ------------------------------------------------------- */

  const loadData = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const results =
          await Promise.allSettled([
            api.get("/stats"),
            api.get("/analytics"),
            api.get("/schools"),
            api.get("/classes"),
            api.get("/products"),
            api.get("/students"),
            api.get("/orders"),
            api.get("/enquiries"),
            api.get("/admin/reviews"),
          ]);

        const [
          statsResult,
          analyticsResult,
          schoolsResult,
          classesResult,
          productsResult,
          studentsResult,
          ordersResult,
          enquiriesResult,
          reviewsResult,
        ] = results;

        if (
          statsResult.status ===
          "fulfilled"
        ) {
          setStats(
            statsResult.value.data || {}
          );
        }

        if (
          analyticsResult.status ===
          "fulfilled"
        ) {
          setAnalytics(
            analyticsResult.value.data || {}
          );
        }

        if (
          schoolsResult.status ===
          "fulfilled"
        ) {
          setSchools(
            safeArray(
              schoolsResult.value.data
            )
          );
        }

        if (
          classesResult.status ===
          "fulfilled"
        ) {
          setClasses(
            safeArray(
              classesResult.value.data
            )
          );
        }

        if (
          productsResult.status ===
          "fulfilled"
        ) {
          setProducts(
            safeArray(
              productsResult.value.data
            )
          );
        }

        if (
          studentsResult.status ===
          "fulfilled"
        ) {
          setStudents(
            safeArray(
              studentsResult.value.data
            )
          );
        }

        if (
          ordersResult.status ===
          "fulfilled"
        ) {
          setOrders(
            safeArray(
              ordersResult.value.data
            )
          );
        }

        if (
          enquiriesResult.status ===
          "fulfilled"
        ) {
          setEnquiries(
            safeArray(
              enquiriesResult.value.data
            )
          );
        }

        if (
          reviewsResult.status ===
          "fulfilled"
        ) {
          setReviews(
            safeArray(
              reviewsResult.value.data
            )
          );
        }

        const failed =
          results.filter(
            (result) =>
              result.status ===
              "rejected"
          );

        if (failed.length === results.length) {
          throw failed[0].reason;
        }
      } catch (err) {
        const message =
          getErrorMessage(err);

        setError(message);

        if (
          err?.response?.status ===
          401
        ) {
          await logout();
          navigate(
            "/admin/login",
            { replace: true }
          );
        } else {
          toast.error(message);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [logout, navigate]
  );

  useEffect(() => {
    if (ready && user) {
      loadData();
    }
  }, [
    ready,
    user,
    loadData,
  ]);

  /* -------------------------------------------------------
     ACTION HELPERS
  ------------------------------------------------------- */

  const closeModal = () => {
    setModal(null);
    setEditing(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate(
      "/admin/login",
      { replace: true }
    );
  };

  const handleDelete = async (
    endpoint,
    label,
    after
  ) => {
    const confirmed =
      window.confirm(
        `Are you sure you want to delete this ${label}? This cannot be undone.`
      );

    if (!confirmed) return;

    try {
      await api.delete(endpoint);

      toast.success(
        `${label} deleted successfully.`
      );

      if (after) after();

      await loadData(true);
    } catch (err) {
      toast.error(
        getErrorMessage(err)
      );
    }
  };

  const updateOrderStatus =
    async (orderId, status) => {
      try {
        await api.put(
          `/orders/${orderId}/status`,
          { status }
        );

        toast.success(
          "Order status updated."
        );

        await loadData(true);
      } catch (err) {
        toast.error(
          getErrorMessage(err)
        );
      }
    };

  const updateReviewStatus =
    async (reviewId, status) => {
      try {
        await api.put(
          `/admin/reviews/${reviewId}/status`,
          { status }
        );

        toast.success(
          "Review status updated."
        );

        await loadData(true);
      } catch (err) {
        toast.error(
          getErrorMessage(err)
        );
      }
    };

  const deleteReview = async (
    reviewId
  ) => {
    await handleDelete(
      `/admin/reviews/${reviewId}`,
      "review"
    );
  };


  /* -------------------------------------------------------
     SCHOOL NAME MAPS
  ------------------------------------------------------- */

  const schoolMap = useMemo(() => {
    const map = {};

    schools.forEach((school) => {
      map[school.id] =
        school.name;
    });

    return map;
  }, [schools]);

  const classMap = useMemo(() => {
    const map = {};

    classes.forEach((item) => {
      map[item.id] =
        `${item.name}${item.section ? ` - ${item.section}` : ""}`;
    });

    return map;
  }, [classes]);


  /* -------------------------------------------------------
     FILTERED DATA
  ------------------------------------------------------- */

  const filteredSchools =
    useMemo(() => {
      const q =
        search.toLowerCase();

      return schools.filter(
        (school) =>
          !q ||
          school.name
            ?.toLowerCase()
            .includes(q) ||
          school.short_name
            ?.toLowerCase()
            .includes(q)
      );
    }, [schools, search]);

  const filteredClasses =
    useMemo(() => {
      const q =
        search.toLowerCase();

      return classes.filter(
        (item) =>
          !q ||
          item.name
            ?.toLowerCase()
            .includes(q) ||
          item.section
            ?.toLowerCase()
            .includes(q) ||
          schoolMap[item.school_id]
            ?.toLowerCase()
            .includes(q)
      );
    }, [
      classes,
      search,
      schoolMap,
    ]);

  const filteredProducts =
    useMemo(() => {
      const q =
        search.toLowerCase();

      return products.filter(
        (product) =>
          !q ||
          product.name
            ?.toLowerCase()
            .includes(q) ||
          product.category
            ?.toLowerCase()
            .includes(q) ||
          schoolMap[
            product.school_id
          ]
            ?.toLowerCase()
            .includes(q)
      );
    }, [
      products,
      search,
      schoolMap,
    ]);

  const filteredStudents =
    useMemo(() => {
      const q =
        search.toLowerCase();

      return students.filter(
        (student) =>
          !q ||
          student.student_name
            ?.toLowerCase()
            .includes(q) ||
          student.parent_name
            ?.toLowerCase()
            .includes(q) ||
          student.phone
            ?.toLowerCase()
            .includes(q) ||
          schoolMap[
            student.school_id
          ]
            ?.toLowerCase()
            .includes(q)
      );
    }, [
      students,
      search,
      schoolMap,
    ]);

  const filteredOrders =
    useMemo(() => {
      const q =
        search.toLowerCase();

      return orders.filter(
        (order) =>
          !q ||
          order.customer_name
            ?.toLowerCase()
            .includes(q) ||
          order.phone
            ?.toLowerCase()
            .includes(q) ||
          order.order_number
            ?.toLowerCase()
            .includes(q) ||
          order.school_name
            ?.toLowerCase()
            .includes(q)
      );
    }, [orders, search]);

  const filteredEnquiries =
    useMemo(() => {
      const q =
        search.toLowerCase();

      return enquiries.filter(
        (item) =>
          !q ||
          item.name
            ?.toLowerCase()
            .includes(q) ||
          item.phone
            ?.toLowerCase()
            .includes(q) ||
          item.email
            ?.toLowerCase()
            .includes(q) ||
          item.message
            ?.toLowerCase()
            .includes(q)
      );
    }, [enquiries, search]);

  const filteredReviews =
    useMemo(() => {
      const q =
        search.toLowerCase();

      return reviews.filter(
        (item) =>
          !q ||
          item.name
            ?.toLowerCase()
            .includes(q) ||
          item.comment
            ?.toLowerCase()
            .includes(q)
      );
    }, [reviews, search]);


  /* -------------------------------------------------------
     NAVIGATION
  ------------------------------------------------------- */

  const navigation = [
    {
      id: "overview",
      label: "Overview",
      icon: LayoutDashboard,
    },
    {
      id: "schools",
      label: "Schools",
      icon: SchoolIcon,
    },
    {
      id: "classes",
      label: "Classes",
      icon: BookOpen,
    },
    {
      id: "products",
      label: "Products",
      icon: Package,
    },
    {
      id: "students",
      label: "Students",
      icon: GraduationCap,
    },
    {
      id: "orders",
      label: "Orders",
      icon: ShoppingCart,
    },
    {
      id: "enquiries",
      label: "Enquiries",
      icon: MessageSquare,
    },
    {
      id: "reviews",
      label: "Reviews",
      icon: Star,
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: BarChart3,
    },
  ];

  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearch("");
    setMobileMenu(false);
  };


  /* -------------------------------------------------------
     LOADING
  ------------------------------------------------------- */

  if (!ready || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <RefreshCw
          className="animate-spin"
          size={28}
        />
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="min-h-screen bg-secondary/30 text-foreground">
      <div className="flex min-h-screen">

        {/* =================================================
            SIDEBAR
        ================================================= */}

        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-50 w-64 border-r border-border bg-background transition-transform lg:static lg:translate-x-0",
            mobileMenu
              ? "translate-x-0"
              : "-translate-x-full"
          )}
        >
          <div className="flex h-full flex-col">

            <div className="flex h-20 items-center justify-between border-b border-border px-5">
              <div>
                <div className="text-lg font-semibold">
                  Uniform Co.
                </div>
                <div className="text-xs text-muted-foreground">
                  Admin Dashboard
                </div>
              </div>

              <button
                className="rounded-lg p-2 hover:bg-secondary lg:hidden"
                onClick={() =>
                  setMobileMenu(false)
                }
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 space-y-1 overflow-y-auto p-3">
              {navigation.map(
                (item) => {
                  const Icon =
                    item.icon;

                  const active =
                    activeTab ===
                    item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() =>
                        changeTab(
                          item.id
                        )
                      }
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition",
                        active
                          ? "bg-foreground text-background"
                          : "hover:bg-secondary"
                      )}
                    >
                      <Icon size={18} />
                      <span>
                        {item.label}
                      </span>
                    </button>
                  );
                }
              )}
            </nav>

            <div className="border-t border-border p-3">
              <div className="mb-3 rounded-lg bg-secondary p-3">
                <div className="truncate text-sm font-medium">
                  {user.email ||
                    "Administrator"}
                </div>

                <div className="text-xs text-muted-foreground">
                  Administrator
                </div>
              </div>

              <Button
                variant="secondary"
                className="w-full"
                onClick={
                  handleLogout
                }
              >
                <LogOut size={17} />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {mobileMenu && (
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() =>
              setMobileMenu(false)
            }
          />
        )}

        {/* =================================================
            MAIN
        ================================================= */}

        <main className="min-w-0 flex-1">

          {/* HEADER */}

          <header className="sticky top-0 z-30 flex h-20 items-center justify-between border-b border-border bg-background/95 px-4 backdrop-blur sm:px-6 lg:px-8">

            <div className="flex items-center gap-3">
              <button
                className="rounded-lg p-2 hover:bg-secondary lg:hidden"
                onClick={() =>
                  setMobileMenu(true)
                }
              >
                <Menu size={22} />
              </button>

              <div>
                <h1 className="text-lg font-semibold">
                  {navigation.find(
                    (item) =>
                      item.id ===
                      activeTab
                  )?.label ||
                    "Dashboard"}
                </h1>

                <p className="hidden text-xs text-muted-foreground sm:block">
                  Manage your Uniform Co. store
                </p>
              </div>
            </div>

            <Button
              variant="secondary"
              onClick={() =>
                loadData(true)
              }
              disabled={refreshing}
            >
              <RefreshCw
                size={16}
                className={
                  refreshing
                    ? "animate-spin"
                    : ""
                }
              />
              <span className="hidden sm:inline">
                Refresh
              </span>
            </Button>
          </header>

          <div className="p-4 sm:p-6 lg:p-8">

            {error && (
              <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                <AlertTriangle
                  size={20}
                  className="mt-0.5 shrink-0"
                />

                <div>
                  <div className="font-medium">
                    Could not load some dashboard data
                  </div>
                  <div className="mt-1 text-sm">
                    {error}
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <LoadingBox />
            ) : (
              <>
                {/* =================================================
                    OVERVIEW
                ================================================= */}

                {activeTab ===
                  "overview" && (
                  <Overview
                    stats={stats}
                    analytics={
                      analytics
                    }
                    orders={orders}
                    products={
                      products
                    }
                    schools={
                      schools
                    }
                    schoolMap={
                      schoolMap
                    }
                    changeTab={
                      changeTab
                    }
                  />
                )}

                {/* =================================================
                    SCHOOLS
                ================================================= */}

                {activeTab ===
                  "schools" && (
                  <SchoolsSection
                    schools={
                      filteredSchools
                    }
                    onAdd={() => {
                      setEditing(
                        null
                      );
                      setModal(
                        "school"
                      );
                    }}
                    onEdit={(
                      school
                    ) => {
                      setEditing(
                        school
                      );
                      setModal(
                        "school"
                      );
                    }}
                    onDelete={(
                      school
                    ) =>
                      handleDelete(
                        `/schools/${school.id}`,
                        "school"
                      )
                    }
                    onSaved={() =>
                      loadData(
                        true
                      )
                    }
                    search={
                      search
                    }
                    setSearch={
                      setSearch
                    }
                  />
                )}

                {/* =================================================
                    CLASSES
                ================================================= */}

                {activeTab ===
                  "classes" && (
                  <ClassesSection
                    classes={
                      filteredClasses
                    }
                    schools={
                      schools
                    }
                    schoolMap={
                      schoolMap
                    }
                    onAdd={() => {
                      setEditing(
                        null
                      );
                      setModal(
                        "class"
                      );
                    }}
                    onEdit={(
                      item
                    ) => {
                      setEditing(
                        item
                      );
                      setModal(
                        "class"
                      );
                    }}
                    onDelete={(
                      item
                    ) =>
                      handleDelete(
                        `/classes/${item.id}`,
                        "class"
                      )
                    }
                    onSaved={() =>
                      loadData(
                        true
                      )
                    }
                    search={
                      search
                    }
                    setSearch={
                      setSearch
                    }
                  />
                )}

                {/* =================================================
                    PRODUCTS
                ================================================= */}

                {activeTab ===
                  "products" && (
                  <ProductsSection
                    products={
                      filteredProducts
                    }
                    schools={
                      schools
                    }
                    schoolMap={
                      schoolMap
                    }
                    onAdd={() => {
                      setEditing(
                        null
                      );
                      setModal(
                        "product"
                      );
                    }}
                    onEdit={(
                      product
                    ) => {
                      setEditing(
                        product
                      );
                      setModal(
                        "product"
                      );
                    }}
                    onDelete={(
                      product
                    ) =>
                      handleDelete(
                        `/products/${product.id}`,
                        "product"
                      )
                    }
                    onSaved={() =>
                      loadData(
                        true
                      )
                    }
                    search={
                      search
                    }
                    setSearch={
                      setSearch
                    }
                  />
                )}

                {/* =================================================
                    STUDENTS
                ================================================= */}

                {activeTab ===
                  "students" && (
                  <StudentsSection
                    students={
                      filteredStudents
                    }
                    schools={
                      schools
                    }
                    classes={
                      classes
                    }
                    schoolMap={
                      schoolMap
                    }
                    classMap={
                      classMap
                    }
                    onAdd={() => {
                      setEditing(
                        null
                      );
                      setModal(
                        "student"
                      );
                    }}
                    onEdit={(
                      student
                    ) => {
                      setEditing(
                        student
                      );
                      setModal(
                        "student"
                      );
                    }}
                    onDelete={(
                      student
                    ) =>
                      handleDelete(
                        `/students/${student.id}`,
                        "student"
                      )
                    }
                    onSaved={() =>
                      loadData(
                        true
                      )
                    }
                    search={
                      search
                    }
                    setSearch={
                      setSearch
                    }
                  />
                )}

                {/* =================================================
                    ORDERS
                ================================================= */}

                {activeTab ===
                  "orders" && (
                  <OrdersSection
                    orders={
                      filteredOrders
                    }
                    onStatusChange={
                      updateOrderStatus
                    }
                    search={
                      search
                    }
                    setSearch={
                      setSearch
                    }
                  />
                )}

                {/* =================================================
                    ENQUIRIES
                ================================================= */}

                {activeTab ===
                  "enquiries" && (
                  <EnquiriesSection
                    enquiries={
                      filteredEnquiries
                    }
                    onDelete={(
                      item
                    ) =>
                      handleDelete(
                        `/enquiries/${item.id}`,
                        "enquiry"
                      )
                    }
                    search={
                      search
                    }
                    setSearch={
                      setSearch
                    }
                  />
                )}

                {/* =================================================
                    REVIEWS
                ================================================= */}

                {activeTab ===
                  "reviews" && (
                  <ReviewsSection
                    reviews={
                      filteredReviews
                    }
                    products={
                      products
                    }
                    onStatusChange={
                      updateReviewStatus
                    }
                    onDelete={
                      deleteReview
                    }
                    search={
                      search
                    }
                    setSearch={
                      setSearch
                    }
                  />
                )}

                {/* =================================================
                    ANALYTICS
                ================================================= */}

                {activeTab ===
                  "analytics" && (
                  <AnalyticsSection
                    analytics={
                      analytics
                    }
                    stats={
                      stats
                    }
                    schoolMap={
                      schoolMap
                    }
                  />
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* =====================================================
          MODALS
      ===================================================== */}

      <SchoolModal
        open={
          modal === "school"
        }
        school={editing}
        onClose={
          closeModal
        }
        onSaved={() =>
          loadData(true)
        }
      />

      <ClassModal
        open={
          modal === "class"
        }
        item={editing}
        schools={
          schools
        }
        onClose={
          closeModal
        }
        onSaved={() =>
          loadData(true)
        }
      />

      <ProductModal
        open={
          modal === "product"
        }
        product={editing}
        schools={
          schools
        }
        onClose={
          closeModal
        }
        onSaved={() =>
          loadData(true)
        }
      />

      <StudentModal
        open={
          modal === "student"
        }
        student={editing}
        schools={
          schools
        }
        classes={
          classes
        }
        onClose={
          closeModal
        }
        onSaved={() =>
          loadData(true)
        }
      />
    </div>
  );
}


/* =========================================================
   OVERVIEW
========================================================= */

function Overview({
  stats,
  analytics,
  orders,
  products,
  schools,
  schoolMap,
  changeTab,
}) {
  const recentOrders =
    [...orders]
      .sort(
        (a, b) =>
          new Date(
            b.created_at || 0
          ) -
          new Date(
            a.created_at || 0
          )
      )
      .slice(0, 6);

  const lowStock =
    safeArray(
      analytics.low_stock_products
    );

  return (
    <div className="space-y-8">

      <div>
        <h2 className="text-2xl font-semibold">
          Welcome back
        </h2>

        <p className="mt-1 text-sm text-muted-foreground">
          Here's what's happening with your store.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={CircleDollarSign}
          label="Revenue"
          value={INR(
            stats.revenue_pipeline
          )}
          subtitle="Paid + confirmed sales"
        />

        <StatCard
          icon={ShoppingCart}
          label="Orders"
          value={
            stats.orders || 0
          }
          subtitle={`${analytics.pending_orders || 0} pending`}
        />

        <StatCard
          icon={Package}
          label="Products"
          value={
            stats.products || 0
          }
          subtitle={`${analytics.low_stock || 0} low stock`}
        />

        <StatCard
          icon={GraduationCap}
          label="Students"
          value={
            stats.students || 0
          }
          subtitle={`${stats.schools || 0} schools`}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-3">

        <div className="rounded-2xl border border-border bg-background p-5 xl:col-span-2">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">
                Recent Orders
              </h3>

              <p className="text-sm text-muted-foreground">
                Latest customer orders
              </p>
            </div>

            <Button
              variant="ghost"
              onClick={() =>
                changeTab(
                  "orders"
                )
              }
            >
              View all
            </Button>
          </div>

          {recentOrders.length ===
          0 ? (
            <EmptyState
              icon={
                ShoppingCart
              }
              title="No orders yet"
              description="Orders will appear here when customers place them."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="pb-3 pr-4">
                      Customer
                    </th>
                    <th className="pb-3 pr-4">
                      School
                    </th>
                    <th className="pb-3 pr-4">
                      Amount
                    </th>
                    <th className="pb-3">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {recentOrders.map(
                    (order) => (
                      <tr
                        key={
                          order.id
                        }
                        className="border-b border-border last:border-0"
                      >
                        <td className="py-3 pr-4">
                          <div className="font-medium">
                            {
                              order.customer_name
                            }
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {order.phone}
                          </div>
                        </td>

                        <td className="py-3 pr-4">
                          {order.school_name ||
                            "—"}
                        </td>

                        <td className="py-3 pr-4 font-medium">
                          {INR(
                            order.total
                          )}
                        </td>

                        <td className="py-3">
                          <StatusBadge
                            status={
                              order.status
                            }
                          />
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-background p-5">
          <div className="mb-5">
            <h3 className="font-semibold">
              Low Stock
            </h3>

            <p className="text-sm text-muted-foreground">
              Products needing attention
            </p>
          </div>

          {lowStock.length ===
          0 ? (
            <div className="flex min-h-[180px] flex-col items-center justify-center text-center">
              <Check
                size={30}
                className="mb-3 text-green-600"
              />

              <div className="font-medium">
                Stock looks good
              </div>

              <div className="mt-1 text-sm text-muted-foreground">
                No products are below their threshold.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {lowStock
                .slice(0, 7)
                .map((item) => (
                  <div
                    key={
                      item.id
                    }
                    className="rounded-xl border border-border p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {
                            item.name
                          }
                        </div>

                        <div className="mt-1 text-xs text-muted-foreground">
                          {schoolMap[
                            item
                              .school_id
                          ] ||
                            "No school"}
                        </div>
                      </div>

                      <AlertTriangle
                        size={17}
                        className="shrink-0 text-yellow-600"
                      />
                    </div>

                    {item.stock !==
                      undefined && (
                      <div className="mt-2 text-xs">
                        Stock:{" "}
                        <strong>
                          {
                            item.stock
                          }
                        </strong>
                      </div>
                    )}

                    {safeArray(
                      item.variants
                    ).map(
                      (
                        variant
                      ) => (
                        <div
                          key={
                            variant.size
                          }
                          className="mt-2 flex justify-between text-xs"
                        >
                          <span>
                            Size{" "}
                            {
                              variant.size
                            }
                          </span>
                          <strong>
                            {
                              variant.stock
                            }
                          </strong>
                        </div>
                      )
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QuickAction
          icon={
            SchoolIcon
          }
          title="Add School"
          onClick={() =>
            changeTab(
              "schools"
            )
          }
        />

        <QuickAction
          icon={Package}
          title="Manage Products"
          onClick={() =>
            changeTab(
              "products"
            )
          }
        />

        <QuickAction
          icon={
            GraduationCap
          }
          title="Manage Students"
          onClick={() =>
            changeTab(
              "students"
            )
          }
        />

        <QuickAction
          icon={
            BarChart3
          }
          title="View Analytics"
          onClick={() =>
            changeTab(
              "analytics"
            )
          }
        />
      </div>
    </div>
  );
}

function QuickAction({
  icon: Icon,
  title,
  onClick,
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 rounded-2xl border border-border bg-background p-5 text-left transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="rounded-xl bg-secondary p-3">
        <Icon size={21} />
      </div>

      <span className="font-medium">
        {title}
      </span>
    </button>
  );
}


/* =========================================================
   SCHOOLS
========================================================= */

function SchoolsSection({
  schools,
  onAdd,
  onEdit,
  onDelete,
  onSaved,
  search,
  setSearch,
}) {
  return (
    <div>
      <SectionHeader
        title="Schools"
        description={`${schools.length} schools`}
        action={
          <Button onClick={onAdd}>
            <Plus size={17} />
            Add School
          </Button>
        }
      />

      <div className="mb-5">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search schools..."
        />
      </div>

      {schools.length === 0 ? (
        <div className="rounded-2xl border border-border bg-background">
          <EmptyState
            icon={
              SchoolIcon
            }
            title="No schools found"
            description="Add your first school to begin managing uniforms."
          />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {schools.map(
            (school) => (
              <div
                key={
                  school.id
                }
                className="rounded-2xl border border-border bg-background p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-11 w-11 rounded-xl border border-border"
                      style={{
                        backgroundColor:
                          school.color ||
                          "#8C9A86",
                      }}
                    />

                    <div>
                      <h3 className="font-semibold">
                        {
                          school.name
                        }
                      </h3>

                      <p className="text-xs text-muted-foreground">
                        {
                          school.short_name
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <p className="mt-4 min-h-10 text-sm text-muted-foreground">
                  {school.description ||
                    "No description."}
                </p>

                <div className="mt-5 flex gap-2 border-t border-border pt-4">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() =>
                      onEdit(
                        school
                      )
                    }
                  >
                    <Pencil
                      size={15}
                    />
                    Edit
                  </Button>

                  <Button
                    variant="danger"
                    onClick={() =>
                      onDelete(
                        school
                      )
                    }
                  >
                    <Trash2
                      size={15}
                    />
                  </Button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}


/* =========================================================
   CLASSES
========================================================= */

function ClassesSection({
  classes,
  schools,
  schoolMap,
  onAdd,
  onEdit,
  onDelete,
  search,
  setSearch,
}) {
  return (
    <div>
      <SectionHeader
        title="Classes"
        description={`${classes.length} classes`}
        action={
          <Button
            onClick={onAdd}
            disabled={
              schools.length ===
              0
            }
          >
            <Plus size={17} />
            Add Class
          </Button>
        }
      />

      {schools.length ===
        0 && (
        <div className="mb-5 rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          Add a school before creating classes.
        </div>
      )}

      <div className="mb-5">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search classes..."
        />
      </div>

      {classes.length ===
      0 ? (
        <div className="rounded-2xl border border-border bg-background">
          <EmptyState
            icon={
              BookOpen
            }
            title="No classes found"
            description="Create classes such as Std 1, Std 2, or Grade 10."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-background">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-left">
                  <th className="px-5 py-3">
                    Class
                  </th>
                  <th className="px-5 py-3">
                    School
                  </th>
                  <th className="px-5 py-3">
                    Academic Year
                  </th>
                  <th className="px-5 py-3">
                    Status
                  </th>
                  <th className="px-5 py-3 text-right">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {classes.map(
                  (item) => (
                    <tr
                      key={
                        item.id
                      }
                      className="border-b border-border last:border-0"
                    >
                      <td className="px-5 py-4 font-medium">
                        {item.name}
                        {item.section &&
                          ` - ${item.section}`}
                      </td>

                      <td className="px-5 py-4">
                        {schoolMap[
                          item
                            .school_id
                        ] ||
                          "—"}
                      </td>

                      <td className="px-5 py-4">
                        {item.academic_year ||
                          "—"}
                      </td>

                      <td className="px-5 py-4">
                        <Badge
                          tone={
                            item.active
                              ? "green"
                              : "red"
                          }
                        >
                          {item.active
                            ? "Active"
                            : "Inactive"}
                        </Badge>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            onClick={() =>
                              onEdit(
                                item
                              )
                            }
                          >
                            <Pencil
                              size={15}
                            />
                          </Button>

                          <Button
                            variant="danger"
                            onClick={() =>
                              onDelete(
                                item
                              )
                            }
                          >
                            <Trash2
                              size={15}
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/* =========================================================
   PRODUCTS
========================================================= */

function ProductsSection({
  products,
  schools,
  schoolMap,
  onAdd,
  onEdit,
  onDelete,
  search,
  setSearch,
}) {
  return (
    <div>
      <SectionHeader
        title="Products"
        description={`${products.length} products`}
        action={
          <Button
            onClick={onAdd}
            disabled={
              schools.length ===
              0
            }
          >
            <Plus size={17} />
            Add Product
          </Button>
        }
      />

      {schools.length ===
        0 && (
        <div className="mb-5 rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          Add a school before creating products.
        </div>
      )}

      <div className="mb-5">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search products, category or school..."
        />
      </div>

      {products.length ===
      0 ? (
        <div className="rounded-2xl border border-border bg-background">
          <EmptyState
            icon={
              Package
            }
            title="No products found"
            description="Add your first uniform product."
          />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {products.map(
            (product) => {
              const variants =
                safeArray(
                  product.variants
                );

              const stock =
                variants.length
                  ? variants.reduce(
                      (
                        total,
                        item
                      ) =>
                        total +
                        Number(
                          item.stock ||
                            0
                        ),
                      0
                    )
                  : Number(
                      product.stock_quantity ||
                        0
                    );

              return (
                <div
                  key={
                    product.id
                  }
                  className="overflow-hidden rounded-2xl border border-border bg-background"
                >
                  <div className="aspect-[4/3] bg-secondary">
                    {product.image ? (
                      <img
                        src={
                          product.image
                        }
                        alt={
                          product.name
                        }
                        className="h-full w-full object-cover"
                        onError={(
                          e
                        ) => {
                          e.currentTarget.style.display =
                            "none";
                        }}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-muted-foreground">
                        <Package
                          size={40}
                        />
                      </div>
                    )}
                  </div>

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">
                          {
                            product.name
                          }
                        </h3>

                        <p className="mt-1 text-xs text-muted-foreground">
                          {
                            schoolMap[
                              product
                                .school_id
                            ]
                          }
                        </p>
                      </div>

                      <Badge>
                        {product.category ||
                          "Uniform"}
                      </Badge>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-lg font-semibold">
                        {INR(
                          product.price
                        )}
                      </span>

                      <span className="text-xs text-muted-foreground">
                        {variants.length
                          ? `${variants.length} sizes`
                          : product.sizes
                              ?.length
                          ? `${product.sizes.length} sizes`
                          : "No sizes"}
                      </span>
                    </div>

                    <div className="mt-2">
                      {product.stock_tracking ? (
                        <Badge
                          tone={
                            stock <=
                            Number(
                              product.low_stock_threshold ||
                                5
                            )
                              ? "yellow"
                              : "green"
                          }
                        >
                          Stock:{" "}
                          {stock}
                        </Badge>
                      ) : (
                        <Badge
                          tone={
                            product.in_stock
                              ? "green"
                              : "red"
                          }
                        >
                          {product.in_stock
                            ? "In stock"
                            : "Out of stock"}
                        </Badge>
                      )}
                    </div>

                    <div className="mt-5 flex gap-2 border-t border-border pt-4">
                      <Button
                        variant="secondary"
                        className="flex-1"
                        onClick={() =>
                          onEdit(
                            product
                          )
                        }
                      >
                        <Pencil
                          size={15}
                        />
                        Edit
                      </Button>

                      <Button
                        variant="danger"
                        onClick={() =>
                          onDelete(
                            product
                          )
                        }
                      >
                        <Trash2
                          size={15}
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}


/* =========================================================
   STUDENTS
========================================================= */

function StudentsSection({
  students,
  schools,
  classes,
  schoolMap,
  classMap,
  onAdd,
  onEdit,
  onDelete,
  search,
  setSearch,
}) {
  return (
    <div>
      <SectionHeader
        title="Students"
        description={`${students.length} students`}
        action={
          <Button
            onClick={onAdd}
            disabled={
              schools.length ===
              0
            }
          >
            <Plus size={17} />
            Add Student
          </Button>
        }
      />

      <div className="mb-5">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search student, parent or phone..."
        />
      </div>

      {students.length ===
      0 ? (
        <div className="rounded-2xl border border-border bg-background">
          <EmptyState
            icon={
              GraduationCap
            }
            title="No students found"
            description="Student records will appear here."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-background">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-left">
                  <th className="px-5 py-3">
                    Student
                  </th>
                  <th className="px-5 py-3">
                    School
                  </th>
                  <th className="px-5 py-3">
                    Class
                  </th>
                  <th className="px-5 py-3">
                    Parent
                  </th>
                  <th className="px-5 py-3">
                    Discount
                  </th>
                  <th className="px-5 py-3">
                    Status
                  </th>
                  <th className="px-5 py-3 text-right">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {students.map(
                  (student) => (
                    <tr
                      key={
                        student.id
                      }
                      className="border-b border-border last:border-0"
                    >
                      <td className="px-5 py-4">
                        <div className="font-medium">
                          {
                            student.student_name
                          }
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {
                            student.phone
                          }
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        {schoolMap[
                          student
                            .school_id
                        ] ||
                          "—"}
                      </td>

                      <td className="px-5 py-4">
                        {classMap[
                          student
                            .class_id
                        ] ||
                          "—"}
                      </td>

                      <td className="px-5 py-4">
                        {
                          student.parent_name
                        }
                      </td>

                      <td className="px-5 py-4">
                        {Number(
                          student.discount_percent ||
                            0
                        )}
                        %
                      </td>

                      <td className="px-5 py-4">
                        <Badge
                          tone={
                            student.active
                              ? "green"
                              : "red"
                          }
                        >
                          {student.active
                            ? "Active"
                            : "Inactive"}
                        </Badge>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            onClick={() =>
                              onEdit(
                                student
                              )
                            }
                          >
                            <Pencil
                              size={15}
                            />
                          </Button>

                          <Button
                            variant="danger"
                            onClick={() =>
                              onDelete(
                                student
                              )
                            }
                          >
                            <Trash2
                              size={15}
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/* =========================================================
   ORDERS
========================================================= */

function OrdersSection({
  orders,
  onStatusChange,
  search,
  setSearch,
}) {
  return (
    <div>
      <SectionHeader
        title="Orders"
        description={`${orders.length} orders`}
      />

      <div className="mb-5">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search customer, phone or order..."
        />
      </div>

      {orders.length ===
      0 ? (
        <div className="rounded-2xl border border-border bg-background">
          <EmptyState
            icon={
              ShoppingCart
            }
            title="No orders found"
            description="Customer orders will appear here."
          />
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(
            (order) => (
              <OrderCard
                key={
                  order.id
                }
                order={
                  order
                }
                onStatusChange={
                  onStatusChange
                }
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

function OrderCard({
  order,
  onStatusChange,
}) {
  const [open, setOpen] =
    useState(false);

  const items =
    safeArray(
      order.items
    );

  return (
    <div className="rounded-2xl border border-border bg-background">
      <div className="p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-secondary p-3">
              <ShoppingCart
                size={21}
              />
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">
                  {order.order_number ||
                    `Order ${String(
                      order.id
                    ).slice(
                      0,
                      8
                    )}`}
                </h3>

                <StatusBadge
                  status={
                    order.status
                  }
                />

                {order.payment_status && (
                  <Badge
                    tone={
                      order.payment_status ===
                      "paid"
                        ? "green"
                        : "yellow"
                    }
                  >
                    Payment:{" "}
                    {
                      order.payment_status
                    }
                  </Badge>
                )}
              </div>

              <p className="mt-1 text-sm text-muted-foreground">
                {
                  order.customer_name
                }{" "}
                •{" "}
                {
                  order.phone
                }
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                {order.school_name ||
                  "No school"}{" "}
                •{" "}
                {formatDateTime(
                  order.created_at
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="text-lg font-semibold">
              {INR(
                order.total
              )}
            </div>

            <select
              value={
                order.status ||
                "pending"
              }
              onChange={(e) =>
                onStatusChange(
                  order.id,
                  e.target.value
                )
              }
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="pending">
                Pending
              </option>
              <option value="confirmed">
                Confirmed
              </option>
              <option value="processing">
                Processing
              </option>
              <option value="shipped">
                Shipped
              </option>
              <option value="delivered">
                Delivered
              </option>
              <option value="cancelled">
                Cancelled
              </option>
            </select>

            <Button
              variant="secondary"
              onClick={() =>
                setOpen(
                  !open
                )
              }
            >
              <Eye
                size={16}
              />
              {open
                ? "Hide"
                : "Details"}
            </Button>
          </div>
        </div>

        {open && (
          <div className="mt-5 border-t border-border pt-5">
            <div className="grid gap-5 lg:grid-cols-2">

              <div>
                <h4 className="mb-3 font-medium">
                  Customer Details
                </h4>

                <div className="space-y-2 rounded-xl bg-secondary/50 p-4 text-sm">
                  <div>
                    <strong>
                      Name:
                    </strong>{" "}
                    {
                      order.customer_name
                    }
                  </div>

                  <div>
                    <strong>
                      Phone:
                    </strong>{" "}
                    {
                      order.phone
                    }
                  </div>

                  <div>
                    <strong>
                      Email:
                    </strong>{" "}
                    {
                      order.email ||
                      "—"
                    }
                  </div>

                  <div>
                    <strong>
                      Address:
                    </strong>{" "}
                    {
                      order.address ||
                      "—"
                    }
                  </div>

                  <div>
                    <strong>
                      School:
                    </strong>{" "}
                    {
                      order.school_name ||
                      "—"
                    }
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-3 font-medium">
                  Order Items
                </h4>

                <div className="space-y-2">
                  {items.map(
                    (
                      item,
                      index
                    ) => (
                      <div
                        key={
                          `${item.product_id}-${index}`
                        }
                        className="flex justify-between rounded-xl border border-border p-3 text-sm"
                      >
                        <div>
                          <div className="font-medium">
                            {
                              item.name
                            }
                          </div>

                          <div className="text-xs text-muted-foreground">
                            {item.size
                              ? `Size: ${item.size} • `
                              : ""}
                            Qty:{" "}
                            {
                              item.quantity
                            }
                          </div>
                        </div>

                        <div className="font-medium">
                          {INR(
                            item.line_total ??
                              Number(
                                item.price ||
                                  0
                              ) *
                                Number(
                                  item.quantity ||
                                    0
                                )
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
}) {
  const tone =
    status === "delivered"
      ? "green"
      : status ===
          "cancelled"
        ? "red"
        : status ===
            "pending"
          ? "yellow"
          : "blue";

  return (
    <Badge tone={tone}>
      {String(
        status ||
          "pending"
      )
        .charAt(0)
        .toUpperCase() +
        String(
          status ||
            "pending"
        ).slice(1)}
    </Badge>
  );
}


/* =========================================================
   ENQUIRIES
========================================================= */

function EnquiriesSection({
  enquiries,
  onDelete,
  search,
  setSearch,
}) {
  return (
    <div>
      <SectionHeader
        title="Enquiries"
        description={`${enquiries.length} enquiries`}
      />

      <div className="mb-5">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search enquiries..."
        />
      </div>

      {enquiries.length ===
      0 ? (
        <div className="rounded-2xl border border-border bg-background">
          <EmptyState
            icon={
              MessageSquare
            }
            title="No enquiries found"
            description="Customer enquiries submitted from your website will appear here."
          />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {enquiries.map(
            (item) => (
              <div
                key={
                  item.id
                }
                className="rounded-2xl border border-border bg-background p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold">
                      {item.name}
                    </h3>

                    <div className="mt-1 text-sm text-muted-foreground">
                      {
                        item.phone
                      }
                    </div>

                    {item.email && (
                      <div className="text-sm text-muted-foreground">
                        {
                          item.email
                        }
                      </div>
                    )}
                  </div>

                  <Button
                    variant="danger"
                    onClick={() =>
                      onDelete(
                        item
                      )
                    }
                  >
                    <Trash2
                      size={15}
                    />
                  </Button>
                </div>

                <div className="mt-4 rounded-xl bg-secondary/50 p-4 text-sm">
                  {
                    item.message ||
                    "No message."
                  }
                </div>

                <div className="mt-3 text-xs text-muted-foreground">
                  {formatDateTime(
                    item.created_at
                  )}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}


/* =========================================================
   REVIEWS
========================================================= */

function ReviewsSection({
  reviews,
  products,
  onStatusChange,
  onDelete,
  search,
  setSearch,
}) {
  const productMap =
    useMemo(() => {
      const map = {};

      products.forEach(
        (product) => {
          map[product.id] =
            product.name;
        }
      );

      return map;
    }, [products]);

  return (
    <div>
      <SectionHeader
        title="Reviews"
        description={`${reviews.length} reviews`}
      />

      <div className="mb-5">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search reviews..."
        />
      </div>

      {reviews.length ===
      0 ? (
        <div className="rounded-2xl border border-border bg-background">
          <EmptyState
            icon={Star}
            title="No reviews found"
            description="Customer product reviews will appear here."
          />
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map(
            (review) => (
              <div
                key={
                  review.id
                }
                className="rounded-2xl border border-border bg-background p-5"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">

                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold">
                        {
                          review.name
                        }
                      </h3>

                      <ReviewStars
                        rating={
                          review.rating
                        }
                      />

                      <Badge
                        tone={
                          review.status ===
                          "approved"
                            ? "green"
                            : review.status ===
                                "rejected"
                              ? "red"
                              : "yellow"
                        }
                      >
                        {
                          review.status
                        }
                      </Badge>
                    </div>

                    <div className="mt-1 text-xs text-muted-foreground">
                      Product:{" "}
                      {
                        productMap[
                          review
                            .product_id
                        ]
                      }
                    </div>

                    <p className="mt-4 text-sm">
                      {
                        review.comment ||
                        "No comment."
                      }
                    </p>

                    <div className="mt-3 text-xs text-muted-foreground">
                      {formatDateTime(
                        review.created_at
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {review.status !==
                      "approved" && (
                      <Button
                        variant="success"
                        onClick={() =>
                          onStatusChange(
                            review.id,
                            "approved"
                          )
                        }
                      >
                        <Check
                          size={15}
                        />
                        Approve
                      </Button>
                    )}

                    {review.status !==
                      "rejected" && (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          onStatusChange(
                            review.id,
                            "rejected"
                          )
                        }
                      >
                        <X
                          size={15}
                        />
                        Reject
                      </Button>
                    )}

                    <Button
                      variant="danger"
                      onClick={() =>
                        onDelete(
                          review.id
                        )
                      }
                    >
                      <Trash2
                        size={15}
                      />
                    </Button>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function ReviewStars({
  rating,
}) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(
        (number) => (
          <Star
            key={number}
            size={14}
            className={
              number <=
              Number(
                rating || 0
              )
                ? "fill-yellow-500 text-yellow-500"
                : "text-muted-foreground"
            }
          />
        )
      )}
    </div>
  );
}


/* =========================================================
   ANALYTICS
========================================================= */

function AnalyticsSection({
  analytics,
  stats,
  schoolMap,
}) {
  const schoolSales =
    safeArray(
      analytics.school_sales
    );

  const productSales =
    safeArray(
      analytics.product_sales
    );

  const dailySales =
    safeArray(
      analytics.daily_sales
    );

  const maxSchool =
    Math.max(
      1,
      ...schoolSales.map(
        (item) =>
          Number(
            item.value || 0
          )
      )
    );

  const maxProduct =
    Math.max(
      1,
      ...productSales.map(
        (item) =>
          Number(
            item.value || 0
          )
      )
    );

  const maxDaily =
    Math.max(
      1,
      ...dailySales.map(
        (item) =>
          Number(
            item.value || 0
          )
      )
    );

  return (
    <div className="space-y-6">

      <SectionHeader
        title="Analytics"
        description="Sales, revenue and inventory performance"
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={
            CircleDollarSign
          }
          label="Total Revenue"
          value={INR(
            analytics.total_revenue
          )}
        />

        <StatCard
          icon={
            Check
          }
          label="Paid Orders"
          value={
            analytics.paid_orders ||
            0
          }
        />

        <StatCard
          icon={
            ClipboardList
          }
          label="Pending Orders"
          value={
            analytics.pending_orders ||
            0
          }
        />

        <StatCard
          icon={
            AlertTriangle
          }
          label="Low Stock"
          value={
            analytics.low_stock ||
            0
          }
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">

        <ChartCard
          title="Sales by School"
          subtitle="Revenue grouped by school"
        >
          {schoolSales.length ===
          0 ? (
            <EmptyState
              icon={
                SchoolIcon
              }
              title="No sales data"
              description="Sales will appear once orders are placed."
            />
          ) : (
            <div className="space-y-4">
              {schoolSales.map(
                (item) => (
                  <div
                    key={
                      item.name
                    }
                  >
                    <div className="mb-1.5 flex justify-between gap-3 text-sm">
                      <span className="truncate">
                        {
                          item.name
                        }
                      </span>

                      <strong>
                        {INR(
                          item.value
                        )}
                      </strong>
                    </div>

                    <div className="h-2 overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full bg-foreground"
                        style={{
                          width: `${Math.max(
                            2,
                            (Number(
                              item.value ||
                                0
                            ) /
                              maxSchool) *
                              100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Top Products"
          subtitle="Revenue by product"
        >
          {productSales.length ===
          0 ? (
            <EmptyState
              icon={
                Package
              }
              title="No product sales"
              description="Product sales will appear here."
            />
          ) : (
            <div className="space-y-4">
              {productSales
                .slice(0, 8)
                .map(
                  (item) => (
                    <div
                      key={
                        item.name
                      }
                    >
                      <div className="mb-1.5 flex justify-between gap-3 text-sm">
                        <span className="truncate">
                          {
                            item.name
                          }
                        </span>

                        <strong>
                          {INR(
                            item.value
                          )}
                        </strong>
                      </div>

                      <div className="h-2 overflow-hidden rounded-full bg-secondary">
                        <div
                          className="h-full rounded-full bg-foreground"
                          style={{
                            width: `${Math.max(
                              2,
                              (Number(
                                item.value ||
                                  0
                              ) /
                                maxProduct) *
                                100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )
                )}
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Daily Sales"
          subtitle="Last 30 days"
          className="xl:col-span-2"
        >
          {dailySales.length ===
          0 ? (
            <EmptyState
              icon={
                TrendingUp
              }
              title="No daily sales"
              description="Daily sales will appear here."
            />
          ) : (
            <div className="flex h-64 items-end gap-2 overflow-x-auto border-b border-border pb-1">
              {dailySales.map(
                (item) => {
                  const height =
                    Math.max(
                      3,
                      (Number(
                        item.value ||
                          0
                      ) /
                        maxDaily) *
                        100
                    );

                  return (
                    <div
                      key={
                        item.date
                      }
                      className="group flex h-full min-w-8 flex-1 flex-col justify-end"
                    >
                      <div className="relative flex h-full items-end">
                        <div
                          className="w-full rounded-t-md bg-foreground transition-opacity group-hover:opacity-70"
                          style={{
                            height: `${height}%`,
                          }}
                          title={`${item.date}: ${INR(item.value)}`}
                        />
                      </div>

                      <div className="mt-2 rotate-45 origin-top-left whitespace-nowrap text-[9px] text-muted-foreground">
                        {
                          item.date
                        }
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}
        </ChartCard>

      </div>

      <div className="rounded-2xl border border-border bg-background p-5">
        <h3 className="font-semibold">
          Low Stock Details
        </h3>

        <p className="mt-1 text-sm text-muted-foreground">
          Items at or below their configured threshold
        </p>

        <div className="mt-5">
          {safeArray(
            analytics.low_stock_products
          ).length ===
          0 ? (
            <div className="rounded-xl bg-secondary/50 p-5 text-center text-sm text-muted-foreground">
              No low-stock products.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {analytics.low_stock_products.map(
                (item) => (
                  <div
                    key={
                      item.id
                    }
                    className="rounded-xl border border-border p-4"
                  >
                    <div className="font-medium">
                      {
                        item.name
                      }
                    </div>

                    <div className="mt-1 text-xs text-muted-foreground">
                      {schoolMap[
                        item
                          .school_id
                      ] ||
                        "—"}
                    </div>

                    {item.stock !==
                      undefined && (
                      <div className="mt-3">
                        <Badge tone="yellow">
                          Stock:{" "}
                          {
                            item.stock
                          }
                        </Badge>
                      </div>
                    )}

                    {safeArray(
                      item.variants
                    ).map(
                      (
                        variant
                      ) => (
                        <div
                          key={
                            variant.size
                          }
                          className="mt-2 flex justify-between text-sm"
                        >
                          <span>
                            Size{" "}
                            {
                              variant.size
                            }
                          </span>
                          <strong>
                            {
                              variant.stock
                            }
                          </strong>
                        </div>
                      )
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
  className = "",
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-background p-5",
        className
      )}
    >
      <div className="mb-5">
        <h3 className="font-semibold">
          {title}
        </h3>

        <p className="mt-1 text-sm text-muted-foreground">
          {subtitle}
        </p>
      </div>

      {children}
    </div>
  );
}


/* =========================================================
   SCHOOL MODAL
========================================================= */

function SchoolModal({
  open,
  school,
  onClose,
  onSaved,
}) {
  const [form, setForm] =
    useState(emptySchool);

  const [saving, setSaving] =
    useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(
      school
        ? {
            name:
              school.name ||
              "",
            short_name:
              school.short_name ||
              "",
            description:
              school.description ||
              "",
            color:
              school.color ||
              "#8C9A86",
          }
        : {
            ...emptySchool,
          }
    );
  }, [open, school]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (school?.id) {
        await api.put(
          `/schools/${school.id}`,
          form
        );

        toast.success(
          "School updated."
        );
      } else {
        await api.post(
          "/schools",
          form
        );

        toast.success(
          "School created."
        );
      }

      onClose();
      onSaved();
    } catch (err) {
      toast.error(
        getErrorMessage(err)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        school
          ? "Edit School"
          : "Add School"
      }
    >
      <form
        onSubmit={save}
        className="space-y-5"
      >
        <Input
          label="School Name"
          value={form.name}
          onChange={(value) =>
            setForm({
              ...form,
              name: value,
            })
          }
          required
        />

        <Input
          label="Short Name"
          value={
            form.short_name
          }
          onChange={(value) =>
            setForm({
              ...form,
              short_name:
                value,
            })
          }
          placeholder="Example: Gurukul"
        />

        <Textarea
          label="Description"
          value={
            form.description
          }
          onChange={(value) =>
            setForm({
              ...form,
              description:
                value,
            })
          }
        />

        <div>
          <label className="mb-2 block text-sm font-medium">
            School Color
          </label>

          <div className="flex gap-3">
            <input
              type="color"
              value={
                form.color ||
                "#8C9A86"
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  color:
                    e.target
                      .value,
                })
              }
              className="h-11 w-16 cursor-pointer rounded-lg border border-border bg-background p-1"
            />

            <Input
              value={
                form.color
              }
              onChange={(value) =>
                setForm({
                  ...form,
                  color:
                    value,
                })
              }
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-5">
          <Button
            variant="secondary"
            onClick={
              onClose
            }
          >
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : "Save School"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}


/* =========================================================
   CLASS MODAL
========================================================= */

function ClassModal({
  open,
  item,
  schools,
  onClose,
  onSaved,
}) {
  const [form, setForm] =
    useState(emptyClass);

  const [saving, setSaving] =
    useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(
      item
        ? {
            school_id:
              item.school_id ||
              "",
            name:
              item.name ||
              "",
            section:
              item.section ||
              "",
            academic_year:
              item.academic_year ||
              "",
            active:
              item.active !==
              false,
          }
        : {
            ...emptyClass,
            school_id:
              schools[0]?.id ||
              "",
          }
    );
  }, [
    open,
    item,
    schools,
  ]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (item?.id) {
        await api.put(
          `/classes/${item.id}`,
          form
        );

        toast.success(
          "Class updated."
        );
      } else {
        await api.post(
          "/classes",
          form
        );

        toast.success(
          "Class created."
        );
      }

      onClose();
      onSaved();
    } catch (err) {
      toast.error(
        getErrorMessage(err)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        item
          ? "Edit Class"
          : "Add Class"
      }
    >
      <form
        onSubmit={save}
        className="space-y-5"
      >
        <SelectField
          label="School"
          value={
            form.school_id
          }
          onChange={(value) =>
            setForm({
              ...form,
              school_id:
                value,
            })
          }
          required
        >
          <option value="">
            Select school
          </option>

          {schools.map(
            (school) => (
              <option
                key={
                  school.id
                }
                value={
                  school.id
                }
              >
                {
                  school.name
                }
              </option>
            )
          )}
        </SelectField>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Class"
            value={
              form.name
            }
            onChange={(value) =>
              setForm({
                ...form,
                name: value,
              })
            }
            placeholder="Example: Standard 1"
            required
          />

          <Input
            label="Section"
            value={
              form.section
            }
            onChange={(value) =>
              setForm({
                ...form,
                section:
                  value,
              })
            }
            placeholder="Example: A"
          />
        </div>

        <Input
          label="Academic Year"
          value={
            form.academic_year
          }
          onChange={(value) =>
            setForm({
              ...form,
              academic_year:
                value,
            })
          }
          placeholder="Example: 2026-27"
        />

        <Checkbox
          label="Class is active"
          checked={
            form.active
          }
          onChange={(value) =>
            setForm({
              ...form,
              active: value,
            })
          }
        />

        <div className="flex justify-end gap-2 border-t border-border pt-5">
          <Button
            variant="secondary"
            onClick={
              onClose
            }
          >
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : "Save Class"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}


/* =========================================================
   PRODUCT MODAL
========================================================= */

function ProductModal({
  open,
  product,
  schools,
  onClose,
  onSaved,
}) {
  const [form, setForm] =
    useState(
      emptyProduct
    );

  const [saving, setSaving] =
    useState(false);

  const [uploadingImage, setUploadingImage] =
  useState(false);

   const mainImageInputRef = useRef(null);
   const galleryImageInputRef = useRef(null);
  
    useEffect(() => {
    if (!open) return;

    if (product) {
      setForm({
        ...emptyProduct,
        ...product,
        sizes: safeArray(
          product.sizes
        ),
        variants:
          safeArray(
            product.variants
          ).map(
            (variant) => ({
              size:
                variant.size ||
                "",
              stock:
                Number(
                  variant.stock ||
                    0
                ),
              price:
                variant.price ===
                null ||
                variant.price ===
                  undefined
                  ? ""
                  : Number(
                      variant.price
                    ),
            })
          ),
        images: safeArray(
          product.images
        ),
        size_chart:
          safeArray(
            product.size_chart
          ),
      });
    } else {
      setForm({
        ...emptyProduct,
        school_id:
          schools[0]?.id ||
          "",
      });
    }
  }, [
    open,
    product,
    schools,
  ]);

  const updateForm = (
    key,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const addVariant = () => {
    setForm((current) => ({
      ...current,
      variants: [
        ...safeArray(
          current.variants
        ),
        {
          size: "",
          stock: 0,
          price: "",
        },
      ],
    }));
  };

  const updateVariant = (
    index,
    key,
    value
  ) => {
    setForm((current) => {
      const variants = [
        ...safeArray(
          current.variants
        ),
      ];

      variants[index] = {
        ...variants[index],
        [key]:
          key === "stock"
            ? Number(
                value || 0
              )
            : key === "price"
              ? value ===
                ""
                ? ""
                : Number(
                    value
                  )
              : value,
      };

      return {
        ...current,
        variants,
      };
    });
  };

  const removeVariant = (
    index
  ) => {
    setForm((current) => ({
      ...current,
      variants:
        safeArray(
          current.variants
        ).filter(
          (_, i) =>
            i !== index
        ),
    }));
  };

  const addImage = () => {
  galleryImageInputRef.current?.click();
  };

  const updateImage = (
    index,
    value
  ) => {
    setForm((current) => {
      const images = [
        ...safeArray(
          current.images
        ),
      ];

      images[index] =
        value;

      return {
        ...current,
        images,
      };
    });
  };

  const removeImage = (
    index
  ) => {
    setForm((current) => ({
      ...current,
      images:
        safeArray(
          current.images
        ).filter(
          (_, i) =>
            i !== index
        ),
    }));
  };

  const uploadProductImage = async (
  file,
  target = "main"
) => {
  if (!file) return;

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
  ];

  if (!allowedTypes.includes(file.type)) {
    toast.error(
      "Please upload JPG, PNG, WEBP, or GIF."
    );
    return;
  }

  const maxSize =
    8 * 1024 * 1024;

  if (file.size > maxSize) {
    toast.error(
      "Image must be smaller than 8 MB."
    );
    return;
  }

  setUploadingImage(true);

  try {
    const dataUrl =
      await new Promise(
        (resolve, reject) => {
          const reader =
            new FileReader();

          reader.onload = () =>
            resolve(
              reader.result
            );

          reader.onerror = () =>
            reject(
              new Error(
                "Could not read image."
              )
            );

          reader.readAsDataURL(
            file
          );
        }
      );

    const response =
      await api.post(
        "/uploads/product-image",
        {
          dataUrl,
        }
      );

    const uploadedUrl =
      response?.data?.url;

    if (!uploadedUrl) {
      throw new Error(
        "Image upload did not return a URL."
      );
    }

    if (target === "main") {
      updateForm(
        "image",
        uploadedUrl
      );

      toast.success(
        "Main image uploaded."
      );
    } else {
      setForm((current) => ({
        ...current,
        images: [
          ...safeArray(
            current.images
          ),
          uploadedUrl,
        ],
      }));

      toast.success(
        "Gallery image uploaded."
      );
    }
  } catch (err) {
    toast.error(
      getErrorMessage(err)
    );
  } finally {
    setUploadingImage(false);
  }
};

const handleMainImageChange = async (
  event
) => {
  const file =
    event.target.files?.[0];

  event.target.value = "";

  await uploadProductImage(
    file,
    "main"
  );
};

const handleGalleryImageChange = async (
  event
) => {
  const file =
    event.target.files?.[0];

  event.target.value = "";

  await uploadProductImage(
    file,
    "gallery"
  );
};

  const addSizeChartRow =
    () => {
      setForm((current) => ({
        ...current,
        size_chart: [
          ...safeArray(
            current.size_chart
          ),
          {
            size: "",
            chest: "",
            length: "",
            waist: "",
          },
        ],
      }));
    };

  const updateSizeChart =
    (
      index,
      key,
      value
    ) => {
      setForm((current) => {
        const rows = [
          ...safeArray(
            current.size_chart
          ),
        ];

        rows[index] = {
          ...rows[index],
          [key]: value,
        };

        return {
          ...current,
          size_chart: rows,
        };
      });
    };

  const removeSizeChart =
    (index) => {
      setForm((current) => ({
        ...current,
        size_chart:
          safeArray(
            current.size_chart
          ).filter(
            (_, i) =>
              i !== index
          ),
      }));
    };

  const save = async (e) => {
    e.preventDefault();

    if (!form.school_id) {
      toast.error(
        "Please select a school."
      );
      return;
    }

    if (!form.name.trim()) {
      toast.error(
        "Product name is required."
      );
      return;
    }

    setSaving(true);

    try {
      const variants =
        safeArray(
          form.variants
        )
          .filter(
            (variant) =>
              String(
                variant.size ||
                  ""
              ).trim()
          )
          .map(
            (variant) => ({
              size: String(
                variant.size
              ).trim(),
              stock: Math.max(
                0,
                Number(
                  variant.stock ||
                    0
                )
              ),
              price:
                variant.price ===
                  "" ||
                variant.price ===
                  null ||
                variant.price ===
                  undefined
                  ? null
                  : Number(
                      variant.price
                    ),
            })
          );

      const images =
        safeArray(
          form.images
        )
          .map((image) =>
            String(
              image || ""
            ).trim()
          )
          .filter(Boolean);

      const sizeChart =
        safeArray(
          form.size_chart
        ).filter(
          (row) =>
            row &&
            String(
              row.size ||
                ""
            ).trim()
        );

      const payload = {
        school_id:
          form.school_id,
        name:
          form.name.trim(),
        category:
          form.category.trim(),
        price: Number(
          form.price || 0
        ),
        sizes: variants.length
          ? variants.map(
              (v) =>
                v.size
            )
          : safeArray(
              form.sizes
            ),
        variants,
        image:
          String(
            form.image ||
              ""
          ).trim(),
        images,
        description:
          form.description ||
          "",
        additional_info:
          form.additional_info ||
          "",
        size_chart:
          sizeChart,
        stock_quantity:
          Math.max(
            0,
            Number(
              form.stock_quantity ||
                0
            )
          ),
        low_stock_threshold:
          Math.max(
            0,
            Number(
              form.low_stock_threshold ||
                0
            )
          ),
        stock_tracking:
          Boolean(
            form.stock_tracking
          ),
        in_stock:
          Boolean(
            form.in_stock
          ),
        order: Number(
          form.order || 0
        ),
      };

      if (product?.id) {
        await api.put(
          `/products/${product.id}`,
          payload
        );

        toast.success(
          "Product updated."
        );
      } else {
        await api.post(
          "/products",
          payload
        );

        toast.success(
          "Product created."
        );
      }

      onClose();
      onSaved();
    } catch (err) {
      toast.error(
        getErrorMessage(err)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        product
          ? "Edit Product"
          : "Add Product"
      }
      wide
    >
      <form
        onSubmit={save}
        className="space-y-8"
      >

        {/* BASIC INFO */}

        <div>
          <h3 className="mb-4 font-semibold">
            Basic Information
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <SelectField
              label="School"
              value={
                form.school_id
              }
              onChange={(
                value
              ) =>
                updateForm(
                  "school_id",
                  value
                )
              }
              required
            >
              <option value="">
                Select school
              </option>

              {schools.map(
                (school) => (
                  <option
                    key={
                      school.id
                    }
                    value={
                      school.id
                    }
                  >
                    {
                      school.name
                    }
                  </option>
                )
              )}
            </SelectField>

            <Input
              label="Product Name"
              value={
                form.name
              }
              onChange={(value) =>
                updateForm(
                  "name",
                  value
                )
              }
              required
            />

            <Input
              label="Category"
              value={
                form.category
              }
              onChange={(value) =>
                updateForm(
                  "category",
                  value
                )
              }
              placeholder="Shirt, Pant, PT Uniform..."
            />

            <Input
              label="Base Price"
              type="number"
              value={
                form.price
              }
              onChange={(value) =>
                updateForm(
                  "price",
                  Number(
                    value || 0
                  )
                )
              }
            />

            <Input
              label="Display Order"
              type="number"
              value={
                form.order
              }
              onChange={(value) =>
                updateForm(
                  "order",
                  Number(
                    value || 0
                  )
                )
              }
            />

           <div className="space-y-2">
  <label className="text-sm font-medium">
    Main Product Image
  </label>

  <div className="flex flex-col gap-3 sm:flex-row">
    <input
      ref={mainImageInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      onChange={handleMainImageChange}
      className="hidden"
    />

    <Button
      type="button"
      variant="secondary"
      onClick={() =>
        mainImageInputRef.current?.click()
      }
      disabled={uploadingImage}
    >
      <Plus size={15} />

      {uploadingImage
        ? "Uploading..."
        : "Upload Main Image"}
    </Button>

    {form.image && (
      <div className="flex items-center gap-3">
        <img
          src={form.image}
          alt="Main product"
          className="h-16 w-16 rounded-lg border border-border object-cover"
        />

        <span className="text-xs text-muted-foreground">
          Image uploaded
        </span>
      </div>
    )}
  </div>
</div>
          </div>
        </div>

        {/* IMAGES */}

<div className="border-t border-border pt-6">
  <input
    ref={galleryImageInputRef}
    type="file"
    accept="image/jpeg,image/png,image/webp,image/gif"
    onChange={handleGalleryImageChange}
    className="hidden"
  />

  <div className="mb-4 flex items-center justify-between">
    <div>
      <h3 className="font-semibold">
        Additional Images
      </h3>

      <p className="text-sm text-muted-foreground">
        Upload additional product images for the gallery.
      </p>
    </div>

    <Button
      type="button"
      variant="secondary"
      onClick={addImage}
      disabled={uploadingImage}
    >
      <Plus size={15} />

      {uploadingImage
        ? "Uploading..."
        : "Add Image"}
    </Button>
  </div>

  {safeArray(form.images).length === 0 ? (
    <div className="rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground">
      No additional images uploaded.
    </div>
  ) : (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {safeArray(form.images).map(
        (image, index) => (
          <div
            key={index}
            className="relative overflow-hidden rounded-xl border border-border bg-secondary"
          >
            <img
              src={image}
              alt={`Product image ${index + 1}`}
              className="aspect-square w-full object-cover"
            />

            <div className="absolute bottom-0 left-0 right-0 flex justify-between gap-2 bg-black/60 p-2">
              <span className="truncate text-xs text-white">
                Image {index + 1}
              </span>

              <Button
                type="button"
                variant="danger"
                onClick={() =>
                  removeImage(index)
                }
              >
                <Trash2 size={15} />
              </Button>
            </div>
          </div>
        )
      )}
    </div>
  )}
</div>

        {/* DESCRIPTION */}

        <div className="border-t border-border pt-6">
          <h3 className="mb-4 font-semibold">
            Product Information
          </h3>

          <div className="space-y-4">
            <Textarea
              label="Description"
              value={
                form.description
              }
              onChange={(value) =>
                updateForm(
                  "description",
                  value
                )
              }
              rows={5}
              placeholder="Describe the product..."
            />

            <Textarea
              label="Additional Information"
              value={
                form.additional_info
              }
              onChange={(value) =>
                updateForm(
                  "additional_info",
                  value
                )
              }
              rows={5}
              placeholder="Fabric, washing instructions, fit, etc."
            />
          </div>
        </div>

        {/* VARIANTS */}

        <div className="border-t border-border pt-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold">
                Size & Stock Variants
              </h3>

              <p className="text-sm text-muted-foreground">
                Each size can have its own stock and price.
              </p>
            </div>

            <Button
              variant="secondary"
              onClick={
                addVariant
              }
            >
              <Plus size={15} />
              Add Size
            </Button>
          </div>

          {safeArray(
            form.variants
          ).length ===
          0 ? (
            <div className="rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground">
              No size variants added. You can still use the base price and product stock.
            </div>
          ) : (
            <div className="space-y-3">
              {safeArray(
                form.variants
              ).map(
                (
                  variant,
                  index
                ) => (
                  <div
                    key={
                      index
                    }
                    className="grid gap-3 rounded-xl border border-border p-4 md:grid-cols-[1fr_1fr_1fr_auto]"
                  >
                    <Input
                      label="Size"
                      value={
                        variant.size
                      }
                      onChange={(
                        value
                      ) =>
                        updateVariant(
                          index,
                          "size",
                          value
                        )
                      }
                      placeholder="28, 30, S, M, L..."
                    />

                    <Input
                      label="Stock"
                      type="number"
                      value={
                        variant.stock
                      }
                      onChange={(
                        value
                      ) =>
                        updateVariant(
                          index,
                          "stock",
                          value
                        )
                      }
                    />

                    <Input
                      label="Price Override"
                      type="number"
                      value={
                        variant.price
                      }
                      onChange={(
                        value
                      ) =>
                        updateVariant(
                          index,
                          "price",
                          value
                        )
                      }
                      placeholder="Optional"
                    />

                    <div className="flex items-end">
                      <Button
                        variant="danger"
                        onClick={() =>
                          removeVariant(
                            index
                          )
                        }
                      >
                        <Trash2
                          size={15}
                        />
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* STOCK */}

        <div className="border-t border-border pt-6">
          <h3 className="mb-4 font-semibold">
            Stock Settings
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Product Stock Quantity"
              type="number"
              value={
                form.stock_quantity
              }
              onChange={(value) =>
                updateForm(
                  "stock_quantity",
                  Number(
                    value || 0
                  )
                )
              }
            />

            <Input
              label="Low Stock Threshold"
              type="number"
              value={
                form.low_stock_threshold
              }
              onChange={(value) =>
                updateForm(
                  "low_stock_threshold",
                  Number(
                    value || 0
                  )
                )
              }
            />
          </div>

          <div className="mt-4 space-y-3">
            <Checkbox
              label="Enable stock tracking"
              checked={
                form.stock_tracking
              }
              onChange={(value) =>
                updateForm(
                  "stock_tracking",
                  value
                )
              }
            />

            <Checkbox
              label="Product is available for sale"
              checked={
                form.in_stock
              }
              onChange={(value) =>
                updateForm(
                  "in_stock",
                  value
                )
              }
            />
          </div>
        </div>

        {/* SIZE CHART */}

        <div className="border-t border-border pt-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">
                Size Chart
              </h3>

              <p className="text-sm text-muted-foreground">
                Optional measurements displayed on product details.
              </p>
            </div>

            <Button
              variant="secondary"
              onClick={
                addSizeChartRow
              }
            >
              <Plus size={15} />
              Add Row
            </Button>
          </div>

          {safeArray(
            form.size_chart
          ).length > 0 && (
            <div className="space-y-3">
              {safeArray(
                form.size_chart
              ).map(
                (
                  row,
                  index
                ) => (
                  <div
                    key={
                      index
                    }
                    className="grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-2 lg:grid-cols-5"
                  >
                    <Input
                      label="Size"
                      value={
                        row.size
                      }
                      onChange={(
                        value
                      ) =>
                        updateSizeChart(
                          index,
                          "size",
                          value
                        )
                      }
                    />

                    <Input
                      label="Chest"
                      value={
                        row.chest
                      }
                      onChange={(
                        value
                      ) =>
                        updateSizeChart(
                          index,
                          "chest",
                          value
                        )
                      }
                    />

                    <Input
                      label="Length"
                      value={
                        row.length
                      }
                      onChange={(
                        value
                      ) =>
                        updateSizeChart(
                          index,
                          "length",
                          value
                        )
                      }
                    />

                    <Input
                      label="Waist"
                      value={
                        row.waist
                      }
                      onChange={(
                        value
                      ) =>
                        updateSizeChart(
                          index,
                          "waist",
                          value
                        )
                      }
                    />

                    <div className="flex items-end">
                      <Button
                        variant="danger"
                        onClick={() =>
                          removeSizeChart(
                            index
                          )
                        }
                      >
                        <Trash2
                          size={15}
                        />
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* SUBMIT */}

        <div className="flex justify-end gap-2 border-t border-border pt-5">
          <Button
            variant="secondary"
            onClick={
              onClose
            }
          >
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : product
                ? "Update Product"
                : "Create Product"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}


/* =========================================================
   STUDENT MODAL
========================================================= */

function StudentModal({
  open,
  student,
  schools,
  classes,
  onClose,
  onSaved,
}) {
  const [form, setForm] =
    useState(
      emptyStudent
    );

  const [saving, setSaving] =
    useState(false);

  const availableClasses =
    useMemo(
      () =>
        classes.filter(
          (item) =>
            !form.school_id ||
            item.school_id ===
              form.school_id
        ),
      [
        classes,
        form.school_id,
      ]
    );

  useEffect(() => {
    if (!open) return;

    if (student) {
      setForm({
        ...emptyStudent,
        ...student,
        discount_percent:
          Number(
            student.discount_percent ||
              0
          ),
      });
    } else {
      setForm({
        ...emptyStudent,
        school_id:
          schools[0]?.id ||
          "",
      });
    }
  }, [
    open,
    student,
    schools,
  ]);

  const save = async (e) => {
    e.preventDefault();

    if (!form.school_id) {
      toast.error(
        "Please select a school."
      );
      return;
    }

    if (!form.student_name.trim()) {
      toast.error(
        "Student name is required."
      );
      return;
    }

    if (!form.phone.trim()) {
      toast.error(
        "Phone number is required."
      );
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        student_name:
          form.student_name.trim(),
        parent_name:
          form.parent_name.trim(),
        phone:
          form.phone.trim(),
        email:
          form.email.trim(),
        address:
          form.address.trim(),
        discount_percent:
          Math.min(
            100,
            Math.max(
              0,
              Number(
                form.discount_percent ||
                  0
              )
            )
          ),
      };

      if (student?.id) {
        await api.put(
          `/students/${student.id}`,
          payload
        );

        toast.success(
          "Student updated."
        );
      } else {
        await api.post(
          "/students",
          payload
        );

        toast.success(
          "Student created."
        );
      }

      onClose();
      onSaved();
    } catch (err) {
      toast.error(
        getErrorMessage(err)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        student
          ? "Edit Student"
          : "Add Student"
      }
      wide
    >
      <form
        onSubmit={save}
        className="space-y-5"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <SelectField
            label="School"
            value={
              form.school_id
            }
            onChange={(value) =>
              setForm({
                ...form,
                school_id:
                  value,
                class_id:
                  "",
              })
            }
            required
          >
            <option value="">
              Select school
            </option>

            {schools.map(
              (school) => (
                <option
                  key={
                    school.id
                  }
                  value={
                    school.id
                  }
                >
                  {
                    school.name
                  }
                </option>
              )
            )}
          </SelectField>

          <SelectField
            label="Class"
            value={
              form.class_id
            }
            onChange={(value) =>
              setForm({
                ...form,
                class_id:
                  value,
              })
            }
          >
            <option value="">
              Select class
            </option>

            {availableClasses.map(
              (item) => (
                <option
                  key={
                    item.id
                  }
                  value={
                    item.id
                  }
                >
                  {item.name}
                  {item.section
                    ? ` - ${item.section}`
                    : ""}
                </option>
              )
            )}
          </SelectField>

          <Input
            label="Student Name"
            value={
              form.student_name
            }
            onChange={(value) =>
              setForm({
                ...form,
                student_name:
                  value,
              })
            }
            required
          />

          <Input
            label="Parent Name"
            value={
              form.parent_name
            }
            onChange={(value) =>
              setForm({
                ...form,
                parent_name:
                  value,
              })
            }
          />

          <Input
            label="Phone"
            value={
              form.phone
            }
            onChange={(value) =>
              setForm({
                ...form,
                phone: value,
              })
            }
            required
          />

          <Input
            label="Email"
            type="email"
            value={
              form.email
            }
            onChange={(value) =>
              setForm({
                ...form,
                email: value,
              })
            }
          />

          <Input
            label="Discount %"
            type="number"
            value={
              form.discount_percent
            }
            onChange={(value) =>
              setForm({
                ...form,
                discount_percent:
                  Number(
                    value || 0
                  ),
              })
            }
          />
        </div>

        <Textarea
          label="Address"
          value={
            form.address
          }
          onChange={(value) =>
            setForm({
              ...form,
              address: value,
            })
          }
        />

        <Checkbox
          label="Student record is active"
          checked={
            form.active
          }
          onChange={(value) =>
            setForm({
              ...form,
              active: value,
            })
          }
        />

        <div className="flex justify-end gap-2 border-t border-border pt-5">
          <Button
            variant="secondary"
            onClick={
              onClose
            }
          >
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : student
                ? "Update Student"
                : "Create Student"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
